/**
 * Parse a date value that came from the API (locale/time-zone audit, lane A06).
 *
 * The API stores UTC. A Postgres `timestamp` read through raw SQL reaches the
 * browser with no zone ('2026-09-26 10:00:00.123'), and `new Date()` reads a
 * zone-less date-time as the VIEWER's local time — so the campaign timeline,
 * the Inbox and ESA coverage showed every instant 4h early in Dubai and 5h30
 * early in India (TZM-F-02). This reads it as UTC:
 *
 *   - 'YYYY-MM-DD[ T]HH:MM[:SS[.fff…]]' with no zone → UTC.
 *   - The same with 'Z', '+04:00', '+0530' or node-postgres's '+00' → that
 *     instant.
 *   - 'YYYY-MM-DD' → a calendar day, returned as UTC MIDNIGHT of that day
 *     (what ECMAScript does for a date-only string). `toISOString().slice(0,
 *     10)` gives the day back; to DISPLAY it, format in UTC, or it shows the
 *     previous day west of Greenwich.
 *   - A number → epoch milliseconds; a Date → itself.
 *   - Anything else `Date` can read (RFC 2822…) → that; otherwise null, never
 *     an Invalid Date.
 *
 * The instant is built from the matched fields, not by `Date`'s own string
 * parser, so it is the same in every browser (Safari rejects the space form).
 *
 * Never pass a local wall clock (a `datetime-local` value, a wizard field)
 * through this — it would be read as UTC. Convert those with `fromZonedTime`.
 */
const ISO_LIKE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:[.,](\d+))?)?\s*(Z|[+-]\d{2}(?::?\d{2})?)?)?$/i

export function parseApiDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value === 'number') {
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
  }
  if (typeof value !== 'string') return null
  const s = value.trim()
  if (!s) return null

  const m = ISO_LIKE.exec(s)
  if (!m) {
    const d = new Date(s)
    return Number.isNaN(d.getTime()) ? null : d
  }

  const [, y, mo, d, hh, mi, ss, frac, zone] = m
  const year = Number(y)
  const month = Number(mo) - 1
  const day = Number(d)
  const hour = hh === undefined ? 0 : Number(hh)
  const minute = mi === undefined ? 0 : Number(mi)
  const second = ss === undefined ? 0 : Number(ss)
  const ms = frac === undefined ? 0 : Number(frac.slice(0, 3).padEnd(3, '0'))
  if (hour > 23 || minute > 59 || second > 59) return null

  const out = new Date(0)
  out.setUTCFullYear(year, month, day)
  out.setUTCHours(hour, minute, second, ms)
  // Reject impossible calendar dates ('2026-02-30') instead of rolling over.
  if (out.getUTCFullYear() !== year || out.getUTCMonth() !== month || out.getUTCDate() !== day) {
    return null
  }

  const offsetMinutes = zoneOffsetMinutes(zone)
  if (offsetMinutes === null) return null
  return new Date(out.getTime() - offsetMinutes * 60_000)
}

/** Minutes east of UTC for 'Z' / '+HH' / '+HHMM' / '+HH:MM'; no zone = UTC. */
function zoneOffsetMinutes(zone: string | undefined): number | null {
  if (!zone || zone.toUpperCase() === 'Z') return 0
  const sign = zone.startsWith('-') ? -1 : 1
  const digits = zone.slice(1).replace(':', '')
  const hours = Number(digits.slice(0, 2))
  const minutes = digits.length > 2 ? Number(digits.slice(2, 4)) : 0
  if (hours > 23 || minutes > 59) return null
  return sign * (hours * 60 + minutes)
}
